import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import Label from '@/app/common/components/Label'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { PlusIcon } from '@/app/common/icons/arrows'
import { parseRequestError } from '@/app/common/utilities'
import usePostSystemCatalogCategory from '@/queries/system_catalog/hooks/usePostSystemCatalogCategory'
import { SystemCatalogCategory } from '@/queries/system_catalog/system_catalog.schema'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group'
import { Dialog, DialogContent, DialogTrigger } from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { z } from 'zod'

export const ProductTypeSchema = z.enum(['service', 'good'])

// Schema factory function that takes categories array and returns a schema with duplicate validation
const createCategorySchema = (categories: SystemCatalogCategory[]) =>
  z.object({
    categoryName: z
      .string()
      .min(2, { message: 'Category name must be at least 2 characters long' })
      .refine(
        (name) =>
          !categories.some(
            (category) => category.name.toLowerCase() === name.toLowerCase()
          ),
        { message: 'Category name already exists' }
      ),
    productType: z.enum(['service', 'good'], {
      required_error: 'Product type is required',
    }),
  })

// TypeScript type for the form values
type CategoryFormValues = {
  categoryName: string
  productType: 'service' | 'good'
}

interface Props {
  onAddCategorySuccess: () => void
  categories: SystemCatalogCategory[]
}

function AddCategoryPopup(props: Props) {
  const { onAddCategorySuccess, categories } = props
  const { toast } = useToast()
  const [open, setOpen] = useState(false)

  // Create the schema using our factory function
  const categorySchema = createCategorySchema(categories)

  const createCategoryMutation = usePostSystemCatalogCategory({
    onSuccess: () => {
      toast({
        title: 'Category Created',
        description: 'The category has been created successfully.',
        variant: 'default',
      })
      onAddCategorySuccess()
      setOpen(false)
      reset()
    },
    onError: (error) => {
      const errorMessage = parseRequestError(error).message
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })

  // Initialize React Hook Form with Zod resolver
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      categoryName: '',
      productType: 'service', // Default to service
    },
    // Update the resolver when categories change
    context: { categories },
  })

  // Form submission handler
  const onSubmit = (data: CategoryFormValues) => {
    createCategoryMutation.mutate({
      name: data.categoryName,
      type: data.productType,
    })
  }

  // Close handler
  const handleClose = () => {
    setOpen(false)
    reset()
  }

  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger>
          <Button className="h-10 w-10 rounded-full">
            <PlusIcon />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <h2 className="font-medium">Add New Category</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label>Category Name</Label>
              <Input
                {...register('categoryName')}
                aria-invalid={errors.categoryName ? 'true' : 'false'}
              />
              {errors.categoryName && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.categoryName.message}
                </p>
              )}
            </div>
            <div>
              <Label>Product Type</Label>
              <Controller
                name="productType"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className=""
                  >
                    <div className="flex items-center space-x-2 mt-1">
                      <RadioGroupItem value="service" id="service" />
                      <Label weight={'normal'} htmlFor="service">
                        Service
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="good" id="good" />
                      <Label weight={'normal'} htmlFor="good">
                        Good
                      </Label>
                    </div>
                  </RadioGroup>
                )}
              />
              {errors.productType && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.productType.message}
                </p>
              )}
            </div>
            <div className="flex space-x-2 pt-2">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <div>
                <SubmitButton
                  buttonTxt="Add Category"
                  status={createCategoryMutation.status}
                />
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AddCategoryPopup
